import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  HelpCircle, Search, ChevronDown, ChevronUp, MessageCircle 
} from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    fetchFAQs();
    fetchCategories();
  }, [selectedCategory]);

  const fetchFAQs = async () => {
    try {
      const url = selectedCategory 
        ? `${API}/faqs?category=${selectedCategory}`
        : `${API}/faqs`;
      const response = await axios.get(url);
      setFaqs(response.data);
    } catch (error) {
      console.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/faqs/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  const filteredFaqs = faqs.filter(faq => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      faq.question?.toLowerCase().includes(searchLower) ||
      faq.answer?.toLowerCase().includes(searchLower)
    );
  });

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-16">
        <div className="container mx-auto px-4 text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-4 opacity-80" />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Find answers to common questions about TheRentPayer insurance coverage
          </p>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        {/* Search and Filter */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search FAQs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 py-6 text-lg"
              data-testid="faq-search"
            />
          </div>
          
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 justify-center">
            <Badge
              variant={selectedCategory === "" ? "default" : "outline"}
              className="cursor-pointer px-4 py-2"
              onClick={() => setSelectedCategory("")}
            >
              All
            </Badge>
            {categories.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="cursor-pointer px-4 py-2"
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredFaqs.length > 0 ? (
            <div className="space-y-4">
              {filteredFaqs.map((faq) => (
                <Card 
                  key={faq.faq_id} 
                  className="card-organic overflow-hidden"
                  data-testid={`faq-${faq.faq_id}`}
                >
                  <button
                    onClick={() => toggleFaq(faq.faq_id)}
                    className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{faq.question}</h3>
                      {faq.category && (
                        <Badge variant="secondary" className="mt-2">
                          {faq.category}
                        </Badge>
                      )}
                    </div>
                    {expandedFaq === faq.faq_id ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === faq.faq_id && (
                    <div className="px-6 pb-6 pt-0 border-t">
                      <p className="text-muted-foreground whitespace-pre-wrap pt-4">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-12 text-center">
                <HelpCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No FAQs found matching your search</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Still have questions? */}
        <div className="max-w-3xl mx-auto mt-12">
          <Card className="card-organic bg-primary/5 border-primary/20">
            <CardContent className="p-8 text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
                Still have questions?
              </h2>
              <p className="text-muted-foreground mb-6">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link to="/support">
                  <Button size="lg" data-testid="contact-support-btn">
                    Contact Support
                  </Button>
                </Link>
                <Link to="/knowledge-base">
                  <Button size="lg" variant="outline" data-testid="browse-kb-btn">
                    Browse Knowledge Base
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
